<script setup lang="ts">
import {onMounted, ref} from "vue";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import {CircleAlert, History, Undo2} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import useAssistantHistory from "@/ai-assistant/composables/useAssistantHistory";
import {Proposition} from "@/ai-assistant/interfaces";
import {CANCEL_CONFIRMATION, PROPOSITION_STATE_LABELS} from "@/ai-assistant/constants";

const {propositions, loading, failed, hasMore, cancelling, failure, load, loadMore, cancel} =
    useAssistantHistory();

/** The one awaiting confirmation. Undoing rewrites the planning. */
const confirming = ref<number | null>(null);

onMounted(load);

const when = (proposition: Proposition): string => {
    const date = proposition.decided_at ?? proposition.generated_at;

    return date ? dayjs(date).locale('fr').format('D MMM YYYY, HH:mm') : '';
};

const scope = (proposition: Proposition): string =>
    proposition.scope
        ? `${dayjs(proposition.scope.start).locale('fr').format('D MMM')} – ${dayjs(proposition.scope.end).locale('fr').format('D MMM')}`
        : '';

/** What produced it, which is what a contestation is answered with. */
const versions = (proposition: Proposition): string => [
    proposition.author?.prompt_version,
    proposition.conformity?.rules_version
].filter(Boolean).join(' · ');

const confirmCancel = async (proposition: Proposition) => {
    if (await cancel(proposition)) {
        confirming.value = null;
    }
};
</script>

<template>
  <section class="min-h-0 overflow-y-auto border-b border-primary-lightest bg-primary-lightest/40 px-4 py-3">
    <div class="mb-1 flex items-center gap-1.5">
      <History class="size-3.5 shrink-0 text-primary" />
      <p class="min-w-0 flex-1 text-xs font-semibold text-primary">
        Historique des propositions
      </p>
    </div>
    <p class="mb-2.5 text-[11px] leading-snug text-gray-dark/70">
      Les scénarios produits pour votre officine et ce qu'il en a été décidé.
      Les conversations, elles, ne sont pas conservées.
    </p>

    <p v-if="loading && !propositions.length" class="text-[11px] text-gray-dark/60">
      Chargement de l'historique…
    </p>

    <div
      v-else-if="failed"
      class="flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
    >
      <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
      L'historique n'a pas pu être chargé. Rouvrez le panneau pour réessayer.
    </div>

    <p v-else-if="!propositions.length" class="text-[11px] leading-snug text-gray-dark/60">
      Aucune proposition pour l'instant.
    </p>

    <ul v-else class="space-y-1.5">
      <li
        v-for="proposition in propositions"
        :key="proposition.id"
        class="rounded-lg border border-primary-lightest bg-white px-2.5 py-2"
      >
        <div class="flex items-start gap-2">
          <div class="min-w-0 flex-1">
            <p class="truncate text-[11px] font-semibold text-gray-dark">
              {{ proposition.author?.origin ?? proposition.type }}
            </p>
            <p class="mt-0.5 text-[10.5px] text-gray-dark/60">
              {{ scope(proposition) }} · {{ when(proposition) }}
              <span v-if="proposition.decided_by"> · {{ proposition.decided_by }}</span>
            </p>
          </div>
          <!--
            An unrecognised state shows itself rather than borrowing a label:
            guessing would assert something about a record that answers a
            contestation.
          -->
          <span class="shrink-0 rounded-full bg-gray-light px-2 py-0.5 text-[10px] text-gray-dark/70">
            {{ PROPOSITION_STATE_LABELS[proposition.state] ?? proposition.state }}
          </span>
        </div>

        <!--
          The versions that produced it. In six months, faced with a
          contestation, this is what answers what the assistant proposed and on
          what basis — the server records it, the screen only has to not lose it.
        -->
        <p v-if="versions(proposition)" class="mt-1 text-[10px] text-gray-dark/50">
          {{ versions(proposition) }}
        </p>

        <template v-if="proposition.state === 'applied'">
          <div v-if="confirming === proposition.id" class="mt-1.5 rounded-lg bg-gray-light px-2 py-1.5">
            <p class="text-[10.5px] leading-snug text-gray-dark/80">
              {{ CANCEL_CONFIRMATION }}
            </p>
            <div class="mt-1.5 flex justify-end gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                class="h-6 text-[11px] text-gray-dark/70"
                @click="confirming = null"
              >
                Non
              </Button>
              <Button
                type="button"
                size="sm"
                class="h-6 bg-primary text-[11px] text-white hover:bg-primary/90"
                :disabled="cancelling === proposition.id"
                @click="confirmCancel(proposition)"
              >
                Rétablir
              </Button>
            </div>
          </div>
          <button
            v-else
            class="mt-1.5 flex items-center gap-1 text-[11px] text-primary/80 transition-colors hover:text-primary"
            @click="confirming = proposition.id"
          >
            <Undo2 class="size-3" />
            Annuler cette modification
          </button>
        </template>
      </li>
    </ul>

    <button
      v-if="hasMore"
      class="mt-1.5 text-[11px] text-primary/80 transition-colors hover:text-primary disabled:opacity-50"
      :disabled="loading"
      @click="loadMore"
    >
      Voir les propositions plus anciennes
    </button>

    <p
      v-if="failure"
      class="mt-2 flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-2 text-[11px] leading-snug text-gray-dark"
    >
      <CircleAlert class="mt-px size-3.5 shrink-0 text-orange" />
      {{ failure }}
    </p>
  </section>
</template>
